import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { paths } from '../routes';
import { useAuth } from './auth.hook';

type QueryString = {
  [key: string]: any;
};

export const useRoutes = () => {
  const history = useHistory();
  const location = useLocation();
  const currentPathname = location.pathname;

  const queryParams = useMemo(() => {
    let params: { [key: string]: any } = {};
    const searchParams = new URLSearchParams(location.search);

    for (var key of searchParams.keys()) {
      params[key] = searchParams.get(key);
    }

    return params;
  }, [location]);

  const navigate = useCallback(
    (path: string) => () => {
      history.push(path);
    },
    [history]
  );

  const goBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const setQueryParams = useCallback(
    (queries: QueryString) => {
      history.push({ search: `?${new URLSearchParams(queries).toString()}` });
    },
    [history]
  );

  const currentPath = useMemo(
    () => Object.values(paths).find(({ path }) => path === location.pathname),
    [location]
  );

  const getPathByTheme = useCallback((themeShortCode) => {
    return Object.values(paths).find((path) => path.themeShortCode === themeShortCode);
  }, []);

  return {
    navigate,
    paths,
    goBack,
    setQueryParams,
    queryParams,
    currentPath,
    getPathByTheme,
    currentPathname
  };
};

type useBlockNavigationType = {
  isLoggedIn: boolean;
  navigateTo: string;
};
export const useBlockNavigation = ({ isLoggedIn, navigateTo }: useBlockNavigationType) => {
  const { navigate } = useRoutes();
  const { isLoggedIn: userLoggedIn } = useAuth();
  useLayoutEffect(() => {
    if (isLoggedIn === userLoggedIn) {
      navigate(navigateTo)();
    }
  }, [navigate, navigateTo, userLoggedIn, isLoggedIn]);
};
